import { createSlice } from "@reduxjs/toolkit";
import { loadPromoSimulatorPersisted } from "../promoSimulatorPersist";
import {
  PROMO_ALL_BRANDS_ID,
  PROMO_ALL_CLASSES_ID,
  PROMO_DEFAULT_MANUFACTURER,
} from "../../Pages/PromoSimulator/services/promoSimulatorService";

const generateId = () =>
  `scenario_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

function applyBatchItemToSimulatorInputs(state, item) {
  if (!item || typeof item !== "object") return;
  if (item.manufacturer != null && String(item.manufacturer).trim()) {
    state.selectedManufacturer = String(item.manufacturer).trim();
  }
  if (item.brand != null && String(item.brand).trim() && item.brand !== "—") {
    state.selectedBrand = String(item.brand).trim();
  }
  if (item.class != null && String(item.class).trim()) {
    state.selectedClass = String(item.class).trim();
  }
  state.selectedSKUs = [];
  state.selectedWeekStart = null;
  state.selectedWeekEnd = null;
}

const persisted = loadPromoSimulatorPersisted();

const initialState = {
  scenarios: persisted?.scenarios ?? [],
  activeScenarioId: persisted?.activeScenarioId ?? null,
  plannerEvents: [],
  plannerInitialized: false,

  currentScenarioId: null,
  savedPlannerScenarios: [], 
  plannerGridData: null, 
  modifiedCells: {},
  cellLoading: {}, 
  plannerCellDrawerOpen: false,
  plannerSelectedCell: null,
  plannerError: null,
  plannerLoading: false,
  planId: null,

  optimizerResults: null,
  optimizerTargetVolume: "",
  optimizerTargetContributionMargin: "",
  optimizerLastTargets: null,
  batchSimulationQueue: [],
  /** Display name of the batch row currently loaded into Scenario Inputs (matches item.sku). */
  batchSimulationActiveSku: null,
  batchOptimizerQueue: [],
  selectedBrand: PROMO_ALL_BRANDS_ID,
  // Empty until catalog loads; ScenarioSimulator / Optimizer sync to manufacturers[0].
  // Ignore persisted "SAVOLA GROUP" so the old hardcoded default does not stick.
  selectedManufacturer:
    persisted?.selectedManufacturer &&
    persisted.selectedManufacturer !== PROMO_DEFAULT_MANUFACTURER
      ? persisted.selectedManufacturer
      : "",
  selectedClass: persisted?.selectedClass ?? PROMO_ALL_CLASSES_ID,
  selectedSKUs: persisted?.selectedSKUs ?? [],
  selectedWeekStart: persisted?.selectedWeekStart ?? null,
  selectedWeekEnd: persisted?.selectedWeekEnd ?? null,
  optimizerWeekStart: null,
  optimizerWeekEnd: null,
  discountPct: persisted?.discountPct ?? 0,
  isLoading: false,
  detailsDrawerOpen: false,
  drawerScenarioId: null,
};

const promoSimulatorSlice = createSlice({
  name: "promoSimulator",
  initialState,
  reducers: {
    setSelectedBrand: (state, action) => {
      state.selectedBrand = action.payload;
    },
    setSelectedClass: (state, action) => {
      state.selectedClass =
        action.payload == null || action.payload === ""
          ? null
          : String(action.payload).trim() || null;
    },
    setSelectedManufacturer: (state, action) => {
      state.selectedManufacturer =
        typeof action.payload === "string" && action.payload.trim()
          ? action.payload.trim()
          : "";
    },
    setSelectedSKUs: (state, action) => {
      state.selectedSKUs = action.payload;
    },
    setSelectedWeekStart: (state, action) => {
      state.selectedWeekStart = action.payload;
    },
    setSelectedWeekEnd: (state, action) => {
      state.selectedWeekEnd = action.payload;
    },
    setOptimizerWeekStart: (state, action) => {
      state.optimizerWeekStart = action.payload;
    },
    setOptimizerWeekEnd: (state, action) => {
      state.optimizerWeekEnd = action.payload;
    },
    setDiscountPct: (state, action) => {
      state.discountPct = action.payload;
    },
    setScenarios: (state, action) => {
      state.scenarios = action.payload;
    },
    addScenario: (state, action) => {
      const scenario = {
        id: generateId(),
        createdAt: new Date().toISOString(),
        ...action.payload,
      };
      state.scenarios.push(scenario);
      state.activeScenarioId = scenario.id;
    },
    updateScenario: (state, action) => {
      const idx = state.scenarios.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) {
        state.scenarios[idx] = { ...state.scenarios[idx], ...action.payload };
      }
    },
    removeScenario: (state, action) => {
      state.scenarios = state.scenarios.filter((s) => s.id !== action.payload);
      if (state.activeScenarioId === action.payload) {
        state.activeScenarioId = null;
      }
    },
    setActiveScenarioId: (state, action) => {
      state.activeScenarioId = action.payload;
    },
    setPlannerEvents: (state, action) => {
      state.plannerEvents = action.payload;
      state.plannerInitialized = true;
    },

    setPlannerLoading: (state, action) => {
      state.plannerLoading = action.payload;
    },
    setPlannerError: (state, action) => {
      state.plannerError = action.payload;
    },
    setSavedPlannerScenarios: (state, action) => {
      state.savedPlannerScenarios = action.payload;
    },
    setCurrentScenarioId: (state, action) => {
      state.currentScenarioId = action.payload;
    },
    setPlannerGridData: (state, action) => {
      state.plannerGridData = action.payload;
      state.currentScenarioId = action.payload?.id ?? null;
      state.modifiedCells = action.payload?.modifiedCells ?? {};
      state.cellLoading = {};
      state.plannerCellDrawerOpen = false;
      state.plannerSelectedCell = null;
      state.plannerError = null;
      state.plannerLoading = false;
    },
    openPlannerCellDrawer: (state, action) => {
      state.plannerCellDrawerOpen = true;
      state.plannerSelectedCell = action.payload;
    },
    closePlannerCellDrawer: (state) => {
      state.plannerCellDrawerOpen = false;
      state.plannerSelectedCell = null;
    },
    markCellModified: (state, action) => {
      const { skuId, week, field, fields } = action.payload;
      if (Array.isArray(fields) && fields.length > 0) {
        fields.forEach((f) => {
          state.modifiedCells[`${skuId}_${week}_${f}`] = true;
        });
        return;
      }
      const safeField = field || "discountPct";
      state.modifiedCells[`${skuId}_${week}_${safeField}`] = true;
    },
    clearModifiedForSku: (state, action) => {
      const skuId = action.payload;
      if (!skuId) return;
      Object.keys(state.modifiedCells || {}).forEach((k) => {
        if (k.startsWith(`${skuId}_`)) delete state.modifiedCells[k];
      });
    },
    setCellLoading: (state, action) => {
      const { skuId, week, loading } = action.payload;
      const key = `${skuId}_${week}`;
      if (loading) state.cellLoading[key] = true;
      else delete state.cellLoading[key];
    },
    updatePlannerCell: (state, action) => {
      const { skuId, week, patch } = action.payload;
      if (!state.plannerGridData) return;

      const row = state.plannerGridData.rows.find((r) => r.skuId === skuId);
      if (!row) return;
      const prev = row.cells[week] || {};
      row.cells[week] = {
        ...prev,
        ...patch,
        kpis: { ...(prev.kpis || {}), ...(patch.kpis || {}) },
      };
    },
    replacePlannerScenarioState: (state, action) => {
      const { gridData, modifiedCells = {} } = action.payload;
      state.plannerGridData = gridData;
      state.currentScenarioId = gridData?.id ?? null;
      state.modifiedCells = modifiedCells;
      state.cellLoading = {};
      state.plannerError = null;
    },
    addPlannerEvent: (state, action) => {
      state.plannerEvents.push({ id: generateId(), ...action.payload });
    },
    removePlannerEvent: (state, action) => {
      state.plannerEvents = state.plannerEvents.filter(
        (e) => e.id !== action.payload
      );
    },
    // ── Batch simulation queue (from Performance History) ──
    setBatchSimulationQueue: (state, action) => {
      const items = Array.isArray(action.payload) ? action.payload : [];
      state.batchSimulationQueue = items;
      state.batchSimulationActiveSku = items.length > 0 ? items[0].sku : null;
      if (items.length > 0) applyBatchItemToSimulatorInputs(state, items[0]);
    },
    selectBatchSimulationSku: (state, action) => {
      const skuName =
        typeof action.payload === "string"
          ? action.payload
          : action.payload?.sku;
      if (!skuName || !Array.isArray(state.batchSimulationQueue)) return;
      const item = state.batchSimulationQueue.find((i) => i.sku === skuName);
      if (!item) return;
      state.batchSimulationActiveSku = skuName;
      applyBatchItemToSimulatorInputs(state, item);
    },
    advanceBatchSimulation: (state) => {
      const q = state.batchSimulationQueue;
      if (!Array.isArray(q) || q.length === 0) return;
      let idx = q.findIndex((i) => i.sku === state.batchSimulationActiveSku);
      if (idx === -1) idx = 0;
      const nextIdx = (idx + 1) % q.length;
      const next = q[nextIdx];
      state.batchSimulationActiveSku = next.sku;
      applyBatchItemToSimulatorInputs(state, next);
    },
    clearBatchSimulationQueue: (state) => {
      state.batchSimulationQueue = [];
      state.batchSimulationActiveSku = null;
    },
    // ── Batch optimizer queue (from Performance History) ──
    setBatchOptimizerQueue: (state, action) => {
      const items = Array.isArray(action.payload) ? action.payload : [];
      state.batchOptimizerQueue = items;
      state.selectedBrand = PROMO_ALL_BRANDS_ID;
    },
    removeFromBatchOptimizerQueue: (state, action) => {
      state.batchOptimizerQueue = state.batchOptimizerQueue.filter(
        (item) => item.sku !== action.payload
      );
    },
    clearBatchOptimizerQueue: (state) => {
      state.batchOptimizerQueue = [];
    },
    setOptimizerResults: (state, action) => {
      state.optimizerResults = action.payload;
    },
    setOptimizerTargetVolume: (state, action) => {
      state.optimizerTargetVolume = action.payload;
    },
    setOptimizerTargetContributionMargin: (state, action) => {
      state.optimizerTargetContributionMargin = action.payload;
    },
    setOptimizerLastTargets: (state, action) => {
      state.optimizerLastTargets = action.payload;
    },
    setPlanId: (state, action) => {
      state.planId = action.payload ?? null;
    },
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    openDetailsDrawer: (state, action) => {
      state.detailsDrawerOpen = true;
      state.drawerScenarioId = action.payload;
    },
    closeDetailsDrawer: (state) => {
      state.detailsDrawerOpen = false;
      state.drawerScenarioId = null;
    },
    resetSimulatorInputs: (state) => {
      state.selectedBrand = PROMO_ALL_BRANDS_ID;
      state.selectedManufacturer = "";
      state.selectedClass = PROMO_ALL_CLASSES_ID;
      state.selectedSKUs = [];
      state.selectedWeekStart = null;
      state.selectedWeekEnd = null;
      state.discountPct = 0;
      state.optimizerResults = null;
      state.optimizerTargetVolume = "";
      state.optimizerTargetContributionMargin = "";
      state.optimizerLastTargets = null;
      /* optimizerWeekStart/End intentionally preserved — Optimizer is independent */
    },
  },
});

export const {
  setSelectedBrand,
  setSelectedClass,
  setSelectedManufacturer,
  setSelectedSKUs,
  setSelectedWeekStart,
  setSelectedWeekEnd,
  setOptimizerWeekStart,
  setOptimizerWeekEnd,
  setDiscountPct,
  setScenarios,
  addScenario,
  updateScenario,
  removeScenario,
  setActiveScenarioId,
  setPlannerEvents,
  addPlannerEvent,
  removePlannerEvent,
  setPlannerLoading,
  setPlannerError,
  setSavedPlannerScenarios,
  setCurrentScenarioId,
  setPlannerGridData,
  openPlannerCellDrawer,
  closePlannerCellDrawer,
  markCellModified,
  clearModifiedForSku,
  setCellLoading,
  updatePlannerCell,
  replacePlannerScenarioState,
  setBatchSimulationQueue,
  selectBatchSimulationSku,
  advanceBatchSimulation,
  clearBatchSimulationQueue,
  setBatchOptimizerQueue,
  removeFromBatchOptimizerQueue,
  clearBatchOptimizerQueue,
  setOptimizerResults,
  setOptimizerTargetVolume,
  setOptimizerTargetContributionMargin,
  setOptimizerLastTargets,
  setPlanId,
  setLoading,
  openDetailsDrawer,
  closeDetailsDrawer,
  resetSimulatorInputs,
} = promoSimulatorSlice.actions;

export default promoSimulatorSlice.reducer;
